import { Draft, PayloadAction } from "@reduxjs/toolkit";

import { AutocompleteItem } from "../../../components/Autocomplete";
import { FormId } from "../../../components/enums";
import { defaultCountry } from "../../../shared";
import {
  createGenericUISlice,
  GenericState,
  UIState,
} from "../../../store/HOC/genericUI.slice";

import { ComponentId as CompanyComponentId } from "./CompanyData/enums";
import { ComponentId as GeneralComponentId } from "./GeneralData/enums";
import { ComponentId as OwnerComponentId } from "./OwnerDetails/enums";
import { ComponentId as PerpetratorComponentId } from "./PerpetratorData/enums";

export type FormDataInputs = {
  [FormId.PERPETRATOR_GENERAL]: {
    [GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_PERPETRATOR]: string;
    [GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_SOBER]: string;
    [GeneralComponentId.PERPETRATOR_GENERAL_IS_KNOWN]: string;
    [GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVING]: string;
    [GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER]: string;
    [GeneralComponentId.PERPETRATOR_GENERAL_ID]: string;
  };
  [FormId.PERPETRATOR_DATA]: {
    [PerpetratorComponentId.PERPETRATOR_NAME]: string;
    [PerpetratorComponentId.PERPETRATOR_SURNAME]: string;
    [PerpetratorComponentId.PERPETRATOR_PHONE]: string;
    [PerpetratorComponentId.PERPETRATOR_EMAIL]: string;
    [PerpetratorComponentId.PERPETRATOR_PESEL]: string;
    [PerpetratorComponentId.PERPETRATOR_STREET]: string;
    [PerpetratorComponentId.PERPETRATOR_STREET_2]: string;
    [PerpetratorComponentId.PERPETRATOR_ZIP_CODE_PL]: string;
    [PerpetratorComponentId.PERPETRATOR_ZIP_CODE_GENERIC]: string;
    [PerpetratorComponentId.PERPETRATOR_CITY]: string;
    [PerpetratorComponentId.PERPETRATOR_COUNTRY]: AutocompleteItem;
    [PerpetratorComponentId.PERPETRATOR_PROVINCE]: string;
    [PerpetratorComponentId.PERPETRATOR_OC_NUMBER]: string;
    [PerpetratorComponentId.PERPETRATOR_INSURANCE_COMPANY]: string;
  };
  [FormId.PERPETRATOR_OWNER]: {
    [OwnerComponentId.PERPETRATOR_OWNER_NAME]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_SURNAME]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_PHONE]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_EMAIL]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_STREET]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_STREET_2]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_ZIP_CODE_PL]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_ZIP_CODE_GENERIC]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_CITY]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_COUNTRY]: AutocompleteItem;
    [OwnerComponentId.PERPETRATOR_OWNER_PROVINCE]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_REGISTRATION_NUMBER]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_MAKE]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_MODEL]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_OC_NUMBER]: string;
    [OwnerComponentId.PERPETRATOR_OWNER_INSURANCE_COMPANY]: string;
  };
  [FormId.PERPETRATOR_COMPANY_DATA]: {
    [CompanyComponentId.PERPETRATOR_COMPANY_NAME]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_REGON]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_PHONE]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_EMAIL]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_STREET]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_STREET_2]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_ZIP_CODE_PL]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_ZIP_CODE_GENERIC]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_CITY]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_COUNTRY]: AutocompleteItem;
    [CompanyComponentId.PERPETRATOR_COMPANY_PROVINCE]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_OC_NUMBER]: string;
    [CompanyComponentId.PERPETRATOR_COMPANY_INSURANCE_COMPANY]: string;
  };
};

export type PerpetratorGeneralFormInputs =
  FormDataInputs[FormId.PERPETRATOR_GENERAL];
export type PerpetratorFormInputs = FormDataInputs[FormId.PERPETRATOR_DATA];
export type PerpetratorOwnerFormInputs =
  FormDataInputs[FormId.PERPETRATOR_OWNER];
export type PerpetratorCompanyDataFormInputs =
  FormDataInputs[FormId.PERPETRATOR_COMPANY_DATA];

type PerpetratorState = {
  data: FormDataInputs;
  ui: UIState;
};

const initialState: PerpetratorState = {
  data: {
    [FormId.PERPETRATOR_GENERAL]: {
      [GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_PERPETRATOR]: "",
      [GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_SOBER]: "",
      [GeneralComponentId.PERPETRATOR_GENERAL_IS_KNOWN]: "",
      [GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVING]: "",
      [GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER]: "",
      [GeneralComponentId.PERPETRATOR_GENERAL_ID]: "",
    },
    [FormId.PERPETRATOR_DATA]: {
      [PerpetratorComponentId.PERPETRATOR_NAME]: "",
      [PerpetratorComponentId.PERPETRATOR_SURNAME]: "",
      [PerpetratorComponentId.PERPETRATOR_PHONE]: "",
      [PerpetratorComponentId.PERPETRATOR_EMAIL]: "",
      [PerpetratorComponentId.PERPETRATOR_PESEL]: "",
      [PerpetratorComponentId.PERPETRATOR_STREET]: "",
      [PerpetratorComponentId.PERPETRATOR_STREET_2]: "",
      [PerpetratorComponentId.PERPETRATOR_ZIP_CODE_PL]: "",
      [PerpetratorComponentId.PERPETRATOR_ZIP_CODE_GENERIC]: "",
      [PerpetratorComponentId.PERPETRATOR_CITY]: "",
      [PerpetratorComponentId.PERPETRATOR_COUNTRY]: {
        ...defaultCountry,
      },
      [PerpetratorComponentId.PERPETRATOR_PROVINCE]: "",
      [PerpetratorComponentId.PERPETRATOR_OC_NUMBER]: "",
      [PerpetratorComponentId.PERPETRATOR_INSURANCE_COMPANY]: "",
    },
    [FormId.PERPETRATOR_OWNER]: {
      [OwnerComponentId.PERPETRATOR_OWNER_NAME]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_SURNAME]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_PHONE]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_EMAIL]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_STREET]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_STREET_2]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_ZIP_CODE_PL]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_ZIP_CODE_GENERIC]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_CITY]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_COUNTRY]: {
        ...defaultCountry,
      },
      [OwnerComponentId.PERPETRATOR_OWNER_PROVINCE]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_REGISTRATION_NUMBER]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_MAKE]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_MODEL]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_OC_NUMBER]: "",
      [OwnerComponentId.PERPETRATOR_OWNER_INSURANCE_COMPANY]: "",
    },
    [FormId.PERPETRATOR_COMPANY_DATA]: {
      [CompanyComponentId.PERPETRATOR_COMPANY_NAME]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_REGON]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_PHONE]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_EMAIL]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_STREET]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_STREET_2]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_ZIP_CODE_PL]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_ZIP_CODE_GENERIC]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_CITY]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_COUNTRY]: {
        ...defaultCountry,
      },
      [CompanyComponentId.PERPETRATOR_COMPANY_PROVINCE]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_OC_NUMBER]: "",
      [CompanyComponentId.PERPETRATOR_COMPANY_INSURANCE_COMPANY]: "",
    },
  },
  ui: {
    validated: false,
    "perpetrator[country]": defaultCountry.value,
    "companyData[country]": defaultCountry.value,
    "owner[country]": defaultCountry.value,
  },
};

const perpetratorSlice = createGenericUISlice({
  name: "@AC/perpetrator",
  initialState: initialState as GenericState<FormDataInputs>,
  reducers: {
    setData: (state, action: PayloadAction<FormDataInputs>) => {
      state.data[FormId.PERPETRATOR_GENERAL] = {
        ...initialState.data[FormId.PERPETRATOR_GENERAL],
        ...action.payload[FormId.PERPETRATOR_GENERAL],
      };
      state.data[FormId.PERPETRATOR_DATA] = {
        ...initialState.data[FormId.PERPETRATOR_DATA],
        ...action.payload[FormId.PERPETRATOR_DATA],
      };
      state.data[FormId.PERPETRATOR_OWNER] = {
        ...initialState.data[FormId.PERPETRATOR_OWNER],
        ...action.payload[FormId.PERPETRATOR_OWNER],
      };
      state.data[FormId.PERPETRATOR_COMPANY_DATA] = {
        ...initialState.data[FormId.PERPETRATOR_COMPANY_DATA],
        ...action.payload[FormId.PERPETRATOR_COMPANY_DATA],
      };
      state.ui.validated = true;
    },
    resetData: (state) => initialState,
    setPerpetrator: (state, action: PayloadAction<string>) => {
      state.data = {
        [FormId.PERPETRATOR_GENERAL]: {
          ...initialState.data[FormId.PERPETRATOR_GENERAL],
          [GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_PERPETRATOR]:
            action.payload,
        },
        [FormId.PERPETRATOR_DATA]: {
          ...initialState.data[FormId.PERPETRATOR_DATA],
        },
        [FormId.PERPETRATOR_OWNER]: {
          ...initialState.data[FormId.PERPETRATOR_OWNER],
        },
        [FormId.PERPETRATOR_COMPANY_DATA]: {
          ...initialState.data[FormId.PERPETRATOR_COMPANY_DATA],
        },
      };
    },
    setAvailableCities: (
      state,
      action: PayloadAction<{ label: string; value: string }[]>
    ) => {
      state.ui["perpetratorAvailableCities"] = action.payload;
    },
    setAvailableModels: (
      state,
      action: PayloadAction<{ label: string; value: string }[]>
    ) => {
      state.ui["perpetratorAvailableModels"] = action.payload;
    },
    setPerpetratorIsKnown: (state, action: PayloadAction<string>) => {
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_IS_KNOWN
      ] = action.payload;
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVING
      ] =
        initialState.data[FormId.PERPETRATOR_GENERAL][
          GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVING
        ];
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER
      ] =
        initialState.data[FormId.PERPETRATOR_GENERAL][
          GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER
        ];
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_ID
      ] =
        initialState.data[FormId.PERPETRATOR_GENERAL][
          GeneralComponentId.PERPETRATOR_GENERAL_ID
        ];
      state.data[FormId.PERPETRATOR_DATA] =
        initialState.data[FormId.PERPETRATOR_DATA];
      state.data[FormId.PERPETRATOR_OWNER] =
        initialState.data[FormId.PERPETRATOR_OWNER];
      state.data[FormId.PERPETRATOR_COMPANY_DATA] =
        initialState.data[FormId.PERPETRATOR_COMPANY_DATA];
    },
    setPerpetratorWasDriving: (state, action: PayloadAction<string>) => {
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVING
      ] = action.payload;
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER
      ] =
        initialState.data[FormId.PERPETRATOR_GENERAL][
          GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER
        ];
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_ID
      ] =
        initialState.data[FormId.PERPETRATOR_GENERAL][
          GeneralComponentId.PERPETRATOR_GENERAL_ID
        ];
      state.data[FormId.PERPETRATOR_DATA] =
        initialState.data[FormId.PERPETRATOR_DATA];
      state.data[FormId.PERPETRATOR_OWNER] =
        initialState.data[FormId.PERPETRATOR_OWNER];
      state.data[FormId.PERPETRATOR_COMPANY_DATA] =
        initialState.data[FormId.PERPETRATOR_COMPANY_DATA];
    },
    setPerpetratorCarOwner: (
      state,
      action: PayloadAction<
        Partial<FormDataInputs[FormId.PERPETRATOR_DATA]> & {
          countryValidator: string;
          [GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER]: string;
        }
      >
    ) => {
      const {
        payload: { carOwner, countryValidator, ...restPayload },
      } = action;
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER
      ] = carOwner;

      if (carOwner === "perpetrator") {
        state.data[FormId.PERPETRATOR_OWNER] = {
          ...state.data[FormId.PERPETRATOR_OWNER],
          ...restPayload,
        };

        if (!("ownerphone" in state.ui)) {
          state.ui.ownerphone = {
            isActive: true,
          };
        }

        state.ui.ownerphone = {
          ...(state.ui.ownerphone as Draft<Record<string, unknown>>),
          country: countryValidator,
        };
      }

      if (carOwner !== "perpetrator") {
        state.data[FormId.PERPETRATOR_OWNER] = {
          ...state.data[FormId.PERPETRATOR_OWNER],
          [PerpetratorComponentId.PERPETRATOR_STREET]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              PerpetratorComponentId.PERPETRATOR_STREET
            ],
          [PerpetratorComponentId.PERPETRATOR_STREET_2]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              PerpetratorComponentId.PERPETRATOR_STREET_2
            ],
          [PerpetratorComponentId.PERPETRATOR_ZIP_CODE_GENERIC]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              PerpetratorComponentId.PERPETRATOR_ZIP_CODE_GENERIC
            ],
          [PerpetratorComponentId.PERPETRATOR_ZIP_CODE_PL]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              PerpetratorComponentId.PERPETRATOR_ZIP_CODE_PL
            ],
          [PerpetratorComponentId.PERPETRATOR_CITY]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              PerpetratorComponentId.PERPETRATOR_CITY
            ],
          [PerpetratorComponentId.PERPETRATOR_COUNTRY]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              PerpetratorComponentId.PERPETRATOR_COUNTRY
            ],
          [PerpetratorComponentId.PERPETRATOR_PROVINCE]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              PerpetratorComponentId.PERPETRATOR_PROVINCE
            ],
        };
      }

      if (carOwner === "otherPerson") {
        state.data[FormId.PERPETRATOR_OWNER] = {
          ...state.data[FormId.PERPETRATOR_OWNER],
          [OwnerComponentId.PERPETRATOR_OWNER_NAME]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              OwnerComponentId.PERPETRATOR_OWNER_NAME
            ],
          [OwnerComponentId.PERPETRATOR_OWNER_SURNAME]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              OwnerComponentId.PERPETRATOR_OWNER_SURNAME
            ],
          [OwnerComponentId.PERPETRATOR_OWNER_PHONE]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              OwnerComponentId.PERPETRATOR_OWNER_PHONE
            ],
          [OwnerComponentId.PERPETRATOR_OWNER_EMAIL]:
            initialState.data[FormId.PERPETRATOR_OWNER][
              OwnerComponentId.PERPETRATOR_OWNER_EMAIL
            ],
        };
      }

      state.ui["owner[country]"] =
        state.data[FormId.PERPETRATOR_OWNER][
          OwnerComponentId.PERPETRATOR_OWNER_COUNTRY
        ].value;
      state.ui["companyData[country]"] =
        state.data[FormId.PERPETRATOR_COMPANY_DATA][
          CompanyComponentId.PERPETRATOR_COMPANY_COUNTRY
        ].value;
    },
    setPerpetratorId: (state, action: PayloadAction<string>) => {
      state.data[FormId.PERPETRATOR_GENERAL][
        GeneralComponentId.PERPETRATOR_GENERAL_ID
      ] = action.payload;
      state.ui["perpetrator[country]"] =
        state.data[FormId.PERPETRATOR_DATA][
          PerpetratorComponentId.PERPETRATOR_COUNTRY
        ].value;
      state.ui["companyData[country]"] =
        state.data[FormId.PERPETRATOR_COMPANY_DATA][
          CompanyComponentId.PERPETRATOR_COMPANY_COUNTRY
        ].value;
    },

    setInsuranceCompany: (state, action: PayloadAction<string>) => {
      state.data[FormId.PERPETRATOR_OWNER][
        OwnerComponentId.PERPETRATOR_OWNER_INSURANCE_COMPANY
      ] = action.payload;
    },
  },
});

export const {
  setData: setPerpetratorFormData,
  resetData: resetPerpetratorData,
  setPerpetrator,
  setPerpetratorId,
  setPerpetratorCarOwner,
  setPerpetratorIsKnown,
  setPerpetratorWasDriving,
  setUIField,
  setUIObjectField,
  setInsuranceCompany,
  setAvailableCities,
  setAvailableModels,
} = perpetratorSlice.actions;

export const { reducer } = perpetratorSlice;
